import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {t, isRTL} from '../../../../../../services/i18n';
import {Colors} from '../../../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import {fetchApi} from '../../../../../../services/HTTP';
import {sortCourseEventDays} from '../../../../../../services/Helpers';
import VStack from '../../../../../common/Layout/VStack';
import HStack from '../../../../../common/Layout/HStack';
import Box from '../../../../../common/Layout/Box';
import AppText from '../../../../../common/AppText';
import FiltersHandler from '../../../../../common/FiltersHandler';
import ChooseHugToSwapSkeleton from '../Steps/ChooseHugToSwapSkeleton';
import InnerStepSection from '../../../../../common/InnerStepSection';
import {getSwapDayFiltersConfig} from '../ChooseSwapDay/swapDayFiltersConfig';
import SwapCourseFromTrack from './SwapCourseFromTrack';
import {
    findSwapEvent,
    formatSwapDayPill,
    getFlatAvailableDays,
    getSwapTargetCourses,
    hasRegisteredSwapDays,
    isTrackSwapWeeklyLimitReached,
} from './trackSwapHelpers';

const ChooseTrackSwapDay = (props) => {
    const {target, values, errors, submitCount, setFieldValue} = props;
    
    const [isLoading, setIsLoading] = useState(true);
    const [swapData, setSwapData] = useState(null);
    const [allowedEventIds, setAllowedEventIds] = useState(null);
    const [filteredToDays, setFilteredToDays] = useState([]);

    const filtersConfig = useMemo(() => getSwapDayFiltersConfig(), []);
    const seriesTrackRelations = useMemo(() => swapData?.seriesTrackRelations ?? [], [swapData]);

    const fromSeriesId = useMemo(() => {
        const relation = seriesTrackRelations.find(relation => relation.series?.current_days?.some(day => day.id === values.fromSeriesEventId));
        return relation?.series_id ?? null;
    }, [seriesTrackRelations, values.fromSeriesEventId]);

    const registeredSeriesTrackRelations = useMemo(
        () => seriesTrackRelations.filter(relation => (relation.series?.current_days?.length ?? 0) > 0),
        [seriesTrackRelations],
    );

    const registeredFromDays = useMemo(() => {
        const days = [];
        registeredSeriesTrackRelations.forEach((relation) => {
            (relation.series?.current_days ?? []).forEach((day) => {
                days.push({...day, id: day.id ?? day.series_event_id});
            });
        });
        return sortCourseEventDays(days);
    }, [registeredSeriesTrackRelations]);

    const availabilityFilterList = useMemo(
        () => getFlatAvailableDays(getSwapTargetCourses(seriesTrackRelations, {fromEventId: values.fromSeriesEventId}),),
        [seriesTrackRelations, values.fromSeriesEventId],
    );

    const targetCourses = useMemo(
        () => getSwapTargetCourses(seriesTrackRelations, {
            fromEventId: values.fromSeriesEventId,
            allowedEventIds,
        }),
        [seriesTrackRelations, values.fromSeriesEventId, allowedEventIds],
    );

    const fromLabel = useMemo(() => {
        const context = findSwapEvent(seriesTrackRelations, values.fromSeriesEventId);
        return context ? formatSwapDayPill(context.seriesTrackRelation, context.day) : null;
    }, [seriesTrackRelations, values.fromSeriesEventId]);

    const toLabel = useMemo(() => {
        const context = findSwapEvent(seriesTrackRelations, values.toSeriesEventId);
        return context ? formatSwapDayPill(context.seriesTrackRelation, context.day) : null;
    }, [seriesTrackRelations, values.toSeriesEventId]);

    const fetchTrackDays = async () => {
        try {
            setIsLoading(true);
            const res = await fetchApi('seriesTrack/getTrackDaysForSwap', 'POST', {membership_user_id: target.membershipUser.id}, false, true,);
            setSwapData(res);
            setFieldValue('swapDayData', res);
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTrackDays();
    }, []);

    useEffect(() => {
        if (!registeredFromDays.length || values.fromSeriesEventId) {
            return;
        }
        setFieldValue('fromSeriesEventId', registeredFromDays[0].id);
    }, [registeredFromDays, values.fromSeriesEventId, setFieldValue]);

    useEffect(() => {
        const isSelectableToDay = (eventId) => {
            if (!filteredToDays.some(day => day.id === eventId)) {
                return false;
            }
            const context = findSwapEvent(seriesTrackRelations, eventId);
            return context && !isTrackSwapWeeklyLimitReached(context.seriesTrackRelation, fromSeriesId);
        };
        if (!filteredToDays.length) {
            if (values.toSeriesEventId) {
                setFieldValue('toSeriesEventId', null);
            }
            return;
        }
        if (!values.toSeriesEventId || !isSelectableToDay(values.toSeriesEventId)) {
            const firstSelectableId = filteredToDays.find(day => isSelectableToDay(day.id))?.id ?? null;
            setFieldValue('toSeriesEventId', firstSelectableId);
        }
    }, [filteredToDays, values.toSeriesEventId, seriesTrackRelations, fromSeriesId, setFieldValue]);

    useEffect(() => {
        if (!values.toSeriesEventId) {
            return;
        }
        const context = findSwapEvent(seriesTrackRelations, values.toSeriesEventId);
        if (!context || !isTrackSwapWeeklyLimitReached(context.seriesTrackRelation, fromSeriesId)) {
            return;
        }
        setFieldValue('toSeriesEventId', null);
    }, [fromSeriesId, values.toSeriesEventId, seriesTrackRelations, setFieldValue]);

    const onSelectFromDay = (eventId) => {
        setFieldValue('fromSeriesEventId', eventId);
        if (values.toSeriesEventId === eventId) {
            setFieldValue('toSeriesEventId', null);
        }
    };

    const onSelectToDay = useCallback((eventId) => {
        setFieldValue('toSeriesEventId', eventId);
    }, [setFieldValue]);

    const showFromError = submitCount > 0 && errors.fromSeriesEventId;
    const showToError = submitCount > 0 && errors.toSeriesEventId;

    if (isLoading) {
        return <ChooseHugToSwapSkeleton/>;
    }

    if (!swapData || !hasRegisteredSwapDays(seriesTrackRelations)) {
        return (
            <VStack space={'1rem'} style={{padding: '2rem 0'}}>
                <AppText fontSize={GlobalStyleAttributes.fonts.standard16}>
                    {t('track-swap-no-days-available')}
                </AppText>
            </VStack>
        );
    }

    return (
        <VStack space={'1.5rem'}>
            <HStack alignItems={'center'} space={'0.5rem'} flexWrap={'wrap'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.darkGrey}>
                    {t('track-swap-track-name-label')}
                </AppText>
                <Box backgroundColor={Colors.newDesign.tableBackgroundColor} borderRadius={'4px'} padding={'5px 10px'} minHeight={'32px'} alignItems={'center'}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.mainText}>
                        {swapData?.track?.name ?? ''}
                    </AppText>
                </Box>
            </HStack>

            <InnerStepSection stepNumber={1} title={t('track-swap-which-day-to-replace')} showConnector>
                {showFromError && (
                    <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.standard16} style={{paddingTop: '8px'}}>
                        {errors.fromSeriesEventId}
                    </AppText>
                )}
                <VStack space={'0'} borderTop={`1px solid ${Colors.borderColor}`} marginTop={'8px'}>
                    {registeredSeriesTrackRelations.map((relation) => (
                        <React.Fragment key={relation.series?.id}>
                            <SwapCourseFromTrack
                                seriesTrackRelation={relation}
                                days={relation.series?.current_days}
                                selectedEventId={values.fromSeriesEventId}
                                onSelect={onSelectFromDay}
                            />
                        </React.Fragment>
                    ))}
                </VStack>
            </InnerStepSection>

            <InnerStepSection
                stepNumber={2}
                title={t('track-swap-select-new-day')}
                headerExtra={(
                    <FiltersHandler
                        list={availabilityFilterList}
                        config={filtersConfig}
                        onFiltered={(filteredDays) => {
                            setFilteredToDays(filteredDays);
                            setAllowedEventIds(filteredDays.map(day => day.id));
                        }}
                    />
                )}
            >
                {fromLabel && (
                    <HStack alignItems={'center'} space={'0.5rem'} flexWrap={'wrap'} style={{paddingTop: '12px', rowGap: '0.5rem'}}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.darkGrey}>
                            {t('track-swap-replacing-label')}
                        </AppText>
                        <Box backgroundColor={Colors.newDesign.tableBackgroundColor} borderRadius={'4px'} padding={'5px 10px'} minHeight={'32px'} alignItems={'center'}>
                            <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.mainText}>
                                {fromLabel}
                            </AppText>
                        </Box>
                        {toLabel && (
                            <>
                                <FontAwesomeIcon
                                    icon={`fa-light fa-arrow-${isRTL() ? 'left' : 'right'}`}
                                    color={Colors.newDesign.darkGrey}
                                    fontSize={GlobalStyleAttributes.fonts.heading4}
                                />
                                <Box backgroundColor={Colors.newDesign.tableBackgroundColor} borderRadius={'4px'} padding={'5px 10px'} minHeight={'32px'} alignItems={'center'}>
                                    <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.mainText}>
                                        {toLabel}
                                    </AppText>
                                </Box>
                            </>
                        )}
                    </HStack>
                )}
                {showToError && (
                    <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.standard16} style={{paddingTop: '8px'}}>
                        {errors.toSeriesEventId}
                    </AppText>
                )}
                <VStack space={'0'} borderTop={`1px solid ${Colors.borderColor}`} marginTop={'8px'}>
                    {targetCourses.map((relation) => (
                        <React.Fragment key={relation.series?.id}>
                            <SwapCourseFromTrack
                                seriesTrackRelation={relation}
                                days={relation.series?.available_days}
                                fromSeriesId={fromSeriesId}
                                selectedEventId={values.toSeriesEventId}
                                onSelect={onSelectToDay}
                                showAvailability
                            />
                        </React.Fragment>
                    ))}
                    {targetCourses.length === 0 && (
                        <AppText fontSize={GlobalStyleAttributes.fonts.standard16} style={{padding: '1.5rem 12px'}}>
                            {t('track-swap-no-days-available')}
                        </AppText>
                    )}
                </VStack>
            </InnerStepSection>
        </VStack>
    );
};

export default ChooseTrackSwapDay;
